/* Native recreation of the joyn AI dashboard shown in the flyer. */
import { Sparkles, Check, Send, Home, Users, Activity, Calendar, MessageCircle, BarChart3, ArrowUp, ArrowDown } from "lucide-react";

const nav = [
  { icon: Home, label: "Home", active: true },
  { icon: Users, label: "Members" },
  { icon: Activity, label: "Engagement" },
  { icon: Calendar, label: "Events" },
  { icon: MessageCircle, label: "Communications" },
  { icon: BarChart3, label: "Dashboards" },
];

export default function AIDashboardMock() {
  return (
    <div className="relative">
      <div className="rounded-2xl overflow-hidden border border-border shadow-2xl bg-white flex">
        {/* Sidebar */}
        <div className="hidden sm:flex flex-col w-40 shrink-0 bg-foreground text-white/70 p-3 gap-1">
          <div className="flex items-center gap-1.5 px-2 py-3 mb-1">
            <svg width="18" height="18" viewBox="0 0 100 100"><g style={{ mixBlendMode: "multiply" }}>
              {[["#D6DF23", 0], ["#4BB5E7", 90], ["#F7931D", 180], ["#F05A28", 270]].map(([f, r], i) => (
                <path key={i} d="M50 6 L74 30 L60 30 L60 44 L74 44 L50 68 L50 44 L36 44 L36 30 L50 30 Z" fill={f} transform={`rotate(${r} 50 50)`} />
              ))}
            </g></svg>
            <span className="font-display font-extrabold text-white text-sm">joyn</span>
          </div>
          {nav.map((n) => (
            <div key={n.label} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[11px] font-medium ${n.active ? "bg-joyn-cyan text-white" : ""}`}>
              <n.icon size={13} /> {n.label}
            </div>
          ))}
        </div>

        {/* Panel */}
        <div className="flex-1 p-4 md:p-5 bg-secondary/40">
          <p className="font-display font-bold text-sm mb-3">Engagement Overview</p>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Engagement Score" value="72" delta="18%" up />
            <Stat label="At Risk Members" value="128" delta="12%" up={false} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {/* Donut */}
            <div className="bg-white rounded-xl border border-border p-3">
              <p className="text-[10px] font-semibold text-muted-foreground mb-2">Engagement by Category</p>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 rounded-full shrink-0" style={{ background: "conic-gradient(#F05A28 0 40%, #4BB5E7 0 70%, #F7931D 0 90%, #3AAA35 0 100%)" }}>
                  <div className="h-8 w-8 bg-white rounded-full m-4" />
                </div>
                <ul className="text-[9px] space-y-0.5 text-muted-foreground">
                  <li><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: "#F05A28" }} />Events 40%</li>
                  <li><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: "#4BB5E7" }} />Education 30%</li>
                  <li><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: "#F7931D" }} />Volunteer 20%</li>
                  <li><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: "#3AAA35" }} />Resources 10%</li>
                </ul>
              </div>
            </div>
            {/* Trend */}
            <div className="bg-white rounded-xl border border-border p-3">
              <p className="text-[10px] font-semibold text-muted-foreground mb-2">Engagement Trend</p>
              <svg viewBox="0 0 120 50" className="w-full h-16">
                <polyline fill="none" stroke="#4BB5E7" strokeWidth="2.5" points="2,40 22,30 42,34 62,18 82,24 102,8 118,14" />
                <polyline fill="url(#g)" stroke="none" points="2,40 22,30 42,34 62,18 82,24 102,8 118,14 118,50 2,50" opacity="0.15" />
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4BB5E7" /><stop offset="100%" stopColor="#fff" /></linearGradient></defs>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Ask Joyn floating card */}
      <div className="absolute -bottom-6 left-4 md:-left-6 w-64 bg-white rounded-2xl border border-border shadow-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg text-white" style={{ background: "linear-gradient(135deg,#4BB5E7,#3AAA35)" }}><Sparkles size={13} /></span>
          <span className="font-bold text-xs">Ask Joyn</span>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug bg-secondary/70 rounded-lg p-2.5">
          Which members haven't renewed in the last 90 days but attended an event?
        </p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] font-medium flex items-center gap-1 text-joyn-green"><Check size={12} /> Here's your answer.</span>
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-white" style={{ background: "#4BB5E7" }}><Send size={11} /></span>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, delta, up }) {
  return (
    <div className="bg-white rounded-xl border border-border p-3">
      <p className="text-[10px] font-semibold text-muted-foreground">{label}</p>
      <div className="flex items-end gap-2 mt-1">
        <span className="font-display font-extrabold text-2xl leading-none">{value}</span>
        <span className={`text-[10px] font-bold flex items-center gap-0.5 ${up ? "text-joyn-green" : "text-joyn-orange"}`}>
          {up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{delta}
        </span>
      </div>
      <p className="text-[9px] text-muted-foreground mt-0.5">vs last 30 days</p>
    </div>
  );
}
